(() => {
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const label = facet => facet.replace(/^named_tags\./, '').split('|').pop();
  const collator = new Intl.Collator(document.documentElement.lang || 'en', { numeric: true, sensitivity: 'base' });
  const compare = (left, right) => {
    const a = left.match(/^(\d+(?:\.\d+)?)\s*(.*)$/), b = right.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
    return a && b && collator.compare(a[2], b[2]) === 0 ? Number(a[1]) - Number(b[1]) || collator.compare(left, right) : collator.compare(left, right);
  };
  const safeUrl = value => {
    if (!value) return '';
    try { const url = new URL(value, window.location.origin); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
  };
  const richText = (markup, inline = false) => {
    const template = document.createElement('template');
    template.innerHTML = String(markup || '');
    template.content.querySelectorAll('script,style,iframe,object,embed').forEach(element => element.remove());
    const allowed = new Set(inline ? ['B', 'STRONG', 'EM', 'I', 'U', 'SPAN', 'BR', 'SUB', 'SUP'] : ['P', 'B', 'STRONG', 'EM', 'I', 'U', 'SPAN', 'BR', 'UL', 'OL', 'LI', 'A', 'SUB', 'SUP']);
    template.content.querySelectorAll('*').forEach(element => {
      if (!allowed.has(element.tagName)) { element.replaceWith(...element.childNodes); return; }
      const href = element.tagName === 'A' ? safeUrl(element.getAttribute('href')) : '';
      [...element.attributes].forEach(attribute => element.removeAttribute(attribute.name));
      if (href) { element.setAttribute('href', href); element.setAttribute('target', '_blank'); element.setAttribute('rel', 'noopener'); }
    });
    return template.content;
  };

  class CollectionFinder extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.revision = (this.revision || 0) + 1;
      this.config = JSON.parse(this.get('config').textContent);
      this.questions = this.config.questions.filter(question => question.facet && question.questiontext);
      this.skipped = new Set();
      this.current = 0;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.onMediaChange = () => this.updateSelectionDisclosure(true);
      this.mobile.addEventListener?.('change', this.onMediaChange);
      this.onClick = this.handleClick.bind(this);
      this.onToggle = () => {
        if (this.get('panel').open && this.initialized && !this.busy && this.needsRefresh) this.loadQuestion();
      };
      this.onReady = () => this.initialize();
      this.addEventListener('click', this.onClick);
      this.get('panel').addEventListener('toggle', this.onToggle);
      document.addEventListener('algolia.instantSearch.afterInit', this.onReady);
      document.addEventListener('algolia.autocomplete.afterInit', this.onReady);
      this.initialize();
      if (!this.initialized) this.readyTimer = setTimeout(() => {
        if (!this.initialized && this.isConnected) this.showError(() => this.initialize(), 'unavailable');
      }, 10000);
    }

    disconnectedCallback() {
      this.connected = false;
      this.initialized = false;
      this.revision++;
      clearTimeout(this.readyTimer);
      this.removeEventListener('click', this.onClick);
      this.get('panel').removeEventListener('toggle', this.onToggle);
      document.removeEventListener('algolia.instantSearch.afterInit', this.onReady);
      document.removeEventListener('algolia.autocomplete.afterInit', this.onReady);
      this.helper?.removeListener('change', this.onStateChange);
      this.helper?.removeListener('result', this.onResults);
      this.search?.removeListener?.('render', this.onRender);
      this.mobile?.removeEventListener?.('change', this.onMediaChange);
    }

    get(name) { return this.querySelector(`[data-cf-${name}]`); }
    t(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, () => String(value)), this.config.labels[key] || key); }
    selected(question, state = this.helper.state) {
      return [...(state.facetsRefinements?.[question.facet] || []), ...(state.disjunctiveFacetsRefinements?.[question.facet] || [])];
    }
    selectionKey() { return JSON.stringify(this.questions.map(question => this.selected(question))); }
    queryKey() {
      const { page, hitsPerPage, ...params } = this.helper.getQuery();
      return JSON.stringify([this.helper.state.index, params]);
    }
    firstUnanswered() { const index = this.questions.findIndex((question, i) => !this.selected(question).length && !this.skipped.has(i)); return index < 0 ? this.questions.length : index; }

    initialize() {
      if (this.initialized) return;
      const search = window.algoliaShopify?.instantsearch?.search;
      const helper = search?.helper;
      if (!helper?.searchForFacetValues || !helper.getQuery || !this.questions.length) return;
      this.helper = helper;
      this.search = search;
      this.initialized = true;
      clearTimeout(this.readyTimer);
      this.current = this.firstUnanswered();
      this.lastSelection = this.selectionKey();
      this.context = this.queryKey();
      if (window.matchMedia('(max-width: 749px)').matches && !this.questions.some(question => this.selected(question).length)) this.get('panel').open = false;
      this.onStateChange = () => {
        if (this.internal) return;
        const context = this.queryKey();
        if (context === this.context) return;
        this.context = context;
        const selected = this.selectionKey();
        if (selected !== this.lastSelection) {
          this.skipped.clear();
          this.current = this.firstUnanswered();
          this.lastSelection = selected;
        }
        this.refresh();
      };
      this.onResults = ({ results }) => this.updateCount(results);
      this.onRender = () => this.updateCount(this.helper.lastResults);
      helper.on('change', this.onStateChange);
      helper.on('result', this.onResults);
      search.on?.('render', this.onRender);
      this.refresh();
      this.updateCount(helper.lastResults);
    }

    updateCount(results) {
      if (!Number.isFinite(results?.nbHits)) return;
      this.get('count').textContent = this.t(results.nbHits === 1 ? 'matches_one' : 'matches_many', { count: results.nbHits });
      this.dataset.hasMatches = String(results.nbHits > 0);
      if (this.current === this.questions.length) this.get('complete').textContent = this.t(results.nbHits ? 'plp_complete' : 'plp_empty');
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.get('status').textContent = busy ? this.t('loading') : '';
      this.querySelectorAll('[data-cf-answer], [data-cf-select], [data-cf-skip]').forEach(control => { control.disabled = busy; });
      const complete = this.current >= this.questions.length;
      this.get('back').hidden = complete;
      this.get('next').hidden = complete;
      this.get('back').disabled = busy || this.current === 0;
      this.get('next').disabled = busy || !this.questionReady || complete || !this.selected(this.questions[this.current]).some(value => this.values?.includes(value));
      this.get('next').textContent = this.t(this.current === this.questions.length - 1 ? 'view_matches' : 'next');
    }

    refresh(focus = false) {
      this.revision++;
      this.questionReady = false;
      this.renderSelections();
      const complete = this.current >= this.questions.length;
      this.updateSelectionDisclosure();
      this.get('step').textContent = complete ? this.t('plp_complete') : this.t('step', { current: this.current + 1, total: this.questions.length });
      this.get('complete').hidden = !complete;
      this.get('question').replaceChildren();
      this.get('help').hidden = true;
      this.get('skip').hidden = complete || this.questions[this.current].mandatory === true;
      this.get('error').hidden = true;
      this.setBusy(false);
      this.needsRefresh = !complete;
      if (complete) {
        this.updateCount(this.helper.lastResults);
        if (focus) this.get('complete').focus({ preventScroll: true });
        return;
      }
      if (this.get('panel').open) this.loadQuestion(focus);
    }

    renderSelections() {
      const selected = this.questions.flatMap((question, index) => this.selected(question).length ? [{ question, index, values: this.selected(question) }] : []);
      this.get('selections').hidden = !selected.length;
      this.get('answers').hidden = !selected.length;
      this.get('answers-label').textContent = this.t('plp_edit_answers', { count: selected.length });
      this.get('reset').hidden = !selected.length && !this.skipped.size;
      this.get('selections').innerHTML = selected.map(({ question, index, values }) => `<button type="button" class="cf-chip" data-cf-edit="${index}" aria-label="${escape(this.t('edit_answer', { label: `${label(question.facet)}: ${values.join(', ')}` }))}"><span>${escape(label(question.facet))}</span><strong>${escape(values.join(', '))}</strong><span aria-hidden="true"><svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 13 13 3M4 3h9v9"/></svg></span></button>`).join('');
    }

    updateSelectionDisclosure(reset = false) {
      const complete = this.current >= this.questions.length;
      const entering = complete && this.dataset.complete !== 'true';
      this.dataset.complete = String(complete);
      if (!complete || !this.mobile.matches) this.get('answers').open = true;
      else if (entering || reset) this.get('answers').open = false;
    }

    async loadQuestion(focus = false) {
      if (this.current >= this.questions.length) return;
      const index = this.current, question = this.questions[index];
      const revision = ++this.revision;
      this.questionReady = false;
      this.needsRefresh = false;
      this.get('error').hidden = true;
      this.get('question').replaceChildren();
      this.get('help').hidden = true;
      this.setBusy(true);
      try {
        let state = this.helper.state;
        this.questions.slice(index).forEach(item => { state = state.clearRefinements(item.facet); });
        const response = await this.helper.searchForFacetValues(question.facet, '', 100, state);
        if (revision !== this.revision || !this.isConnected) return;
        if (!Array.isArray(response?.facetHits)) throw new Error('Invalid facet response');
        this.values = [...new Set(response.facetHits.map(hit => hit.value).filter(value => typeof value === 'string' && value.trim()))].sort(compare);
        this.renderQuestion(question);
        this.questionReady = true;
        this.setBusy(false);
        if (focus) this.get('question').querySelector('legend')?.focus({ preventScroll: true });
      } catch {
        if (revision !== this.revision || !this.isConnected) return;
        this.setBusy(false);
        this.showError(() => this.loadQuestion(focus));
      }
    }

    renderQuestion(question) {
      const selected = this.selected(question);
      const options = this.values.map(value => `<button type="button" class="cf-answer" data-cf-answer="${escape(value)}" aria-pressed="${selected.includes(value)}"><span>${escape(value)}</span><svg class="cf-answer-check" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="m3 8 3 3 7-7"/></svg></button>`).join('');
      this.get('question').innerHTML = `<fieldset${this.values.length ? ` aria-describedby="${this.id}-hint"` : ''}><legend tabindex="-1"></legend>${this.values.length ? `<p class="cf-hint" id="${this.id}-hint">${escape(this.t('plp_multiple_hint'))}</p><div class="cf-answers${question.optiondisplaytype === 'dropdown' ? ' cf-answers--list' : ''}">${options}</div>` : `<p class="cf-empty">${escape(this.t('plp_empty'))}</p>`}</fieldset>`;
      this.get('question').querySelector('legend').append(richText(question.questiontext, true));
      const helper = this.get('helper-content');
      helper.replaceChildren();
      const url = safeUrl(question.longhelperblogurl);
      const html = String(question.shorthelpertext || '').replace('(longhelperblogurl)', url ? `<a href="${escape(url)}">${escape(question.longhelperblogurltext)}</a>` : escape(question.longhelperblogurltext));
      helper.append(richText(html));
      const image = safeUrl(question.shorthelperimageurl);
      if (image) {
        const element = document.createElement('img');
        element.src = image; element.alt = label(question.facet); element.loading = 'lazy'; element.width = 400; element.height = 300;
        helper.append(element);
      }
      this.get('help').hidden = !html && !image;
    }

    commit(state) {
      this.internal = true;
      try { this.helper.setState(state.setPage(0)).search(); } finally { this.internal = false; }
      this.lastSelection = this.selectionKey();
      this.context = this.queryKey();
    }

    answer(value) {
      if (this.busy || !this.questionReady) return;
      const question = this.questions[this.current];
      const selected = this.selected(question);
      if (value !== null && !this.values.includes(value)) return;
      const values = value === null ? [] : selected.includes(value) ? selected.filter(item => item !== value) : [...selected, value];
      if (value !== null || selected.length) {
        let state = this.helper.state;
        this.questions.slice(this.current).forEach(item => { state = state.clearRefinements(item.facet); });
        state = state.removeFacet(question.facet).addDisjunctiveFacet(question.facet);
        values.forEach(item => { state = state.addDisjunctiveFacetRefinement(question.facet, item); });
        this.skipped = new Set([...this.skipped].filter(index => index < this.current));
        this.commit(state);
      }
      if (value === null) {
        this.skipped.add(this.current);
        this.advance();
        return;
      }
      this.skipped.delete(this.current);
      this.renderSelections();
      this.querySelectorAll('[data-cf-answer]').forEach(button => { button.setAttribute('aria-pressed', String(values.includes(button.dataset.cfAnswer))); });
      this.setBusy(false);
    }

    advance() {
      this.current++;
      this.get('help').open = false;
      this.refresh(true);
    }

    showError(retry, key = 'search_error') { this.retry = retry; this.get('error-message').textContent = this.t(key); this.get('error').hidden = false; }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.hasAttribute('data-cf-view')) {
        const results = document.querySelector('.ais-hits-container');
        if (results) {
          results.setAttribute('tabindex', '-1');
          results.style.scrollMarginTop = `${(document.getElementById('shopify-section-header')?.getBoundingClientRect().bottom || 0) + 16}px`;
          results.focus({ preventScroll: true });
          results.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
        return;
      }
      if (button.hasAttribute('data-cf-reset')) {
        let state = this.helper.state;
        this.questions.forEach(question => { state = state.clearRefinements(question.facet); });
        this.skipped.clear(); this.current = 0; this.commit(state); this.refresh(true); return;
      }
      if (button.hasAttribute('data-cf-edit')) { this.current = Number(button.dataset.cfEdit); this.refresh(true); return; }
      if (this.busy) return;
      if (button.hasAttribute('data-cf-retry')) { this.retry?.(); return; }
      if (button.hasAttribute('data-cf-answer')) { this.answer(button.dataset.cfAnswer); return; }
      if (button.hasAttribute('data-cf-skip')) { this.answer(null); return; }
      if (button.hasAttribute('data-cf-back')) { this.current = Math.max(0, this.current - 1); this.refresh(true); return; }
      if (button.hasAttribute('data-cf-next') && this.questionReady && !this.get('next').disabled) this.advance();
    }
  }
  if (!customElements.get('mtbd-collection-finder')) customElements.define('mtbd-collection-finder', CollectionFinder);
})();
